import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import matchService from "../../services/api/matchService";
import { getAllSports, getUserSports } from "../../services/api/sportsService";
import { userService } from "../../services/api/userService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import { useAuth } from "../../contexts/AuthContext";
import VenueSearch from "../ui/VenueSearch";
import { eid } from "../../utils/entityId";
import { MATCH_ADMIN_ROLES, normalizeUserRole } from "../../constants/roles";
import { buildMatchUpdatePayload } from "../../utils/matchFormUtils";
import { eventTimeZone, toEventInputValue } from "../../utils/eventTime";

const MatchEdit = ({ onLogout }) => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  

  
  const [match, setMatch] = useState(null);
  const [sports, setSports] = useState([]);
  const [users, setUsers] = useState([]);
  
  const [formData, setFormData] = useState({
    match_name: "",
    sport_id: "",
    tournament_id: null,
    participant_1_id: "",
    participant_2_id: "",
    scheduled_at: "",
    venue: "",
    venue_address: "",
    venue_lat: null,
    venue_lng: null,
    venue_place_id: "",
    court: "",
    description: "",
    referee_id: "",
    status: "scheduled"
  });
  // The form as loaded, so only the fields the host changed are sent (MT-B19)
  const [initialForm, setInitialForm] = useState(null);
  // Only a scheduled match can be rescheduled or moved; notes stay editable
  const isLocked = !!match && (match.status || formData.status) !== "scheduled";

  // Load initial data
  useEffect(() => {
    // Reset all state when matchId changes
    setMatch(null);
    setError("");
    setSuccess("");
    setLoading(true);
    
    const loadData = async () => {

      
      if (!matchId) {
        setError("Match ID is missing from URL parameters");
        setLoading(false);
        return;
      }

      if (!isAuthenticated) {
        setError("You must be logged in to edit a match.");
        setLoading(false);
        return;
      }

      if (!user || !user.id) {
        setError("User information is missing. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Validate matchId before making API call
        if (!matchId || matchId === 'undefined' || matchId === 'null') {
          setError(`Invalid match ID: ${matchId}. Please check the URL.`);
          setLoading(false);
          return;
        }

        // Load match data first (most critical)
        const matchResponse = await matchService.getMatchById(matchId);

        // Load sports data: prefer user sports (scoped), fall back to all sports
        let sportsResponse = { data: [] };
        try {
          const userSportsRes = await getUserSports();
          const userSportsData = userSportsRes?.data?.sports || userSportsRes?.sports || userSportsRes?.data || [];
          if (userSportsData.length) {
            sportsResponse = { data: userSportsData };
          } else {
            sportsResponse = await getAllSports();
          }
        } catch (sportError) {
          console.warn("Failed to load user sports, falling back:", sportError);
          try { sportsResponse = await getAllSports(); } catch { /* ignore */ }
        }

        // Load users data (optional for edit form)
        let usersResponse = { data: [] };
        try {
          usersResponse = await userService.getAllUsers();
        } catch (userError) {
          // Continue without user data - we can still edit basic match info
        }

        if (matchResponse && matchResponse.success === true && matchResponse.data) {
          const matchData = matchResponse.data;

          // Permission check: only the match creator and admins may edit.
          // Prefer the backend's own verdict; fall back to the same rule.
          const isAdmin = MATCH_ADMIN_ROLES.includes(normalizeUserRole(user.role));
          const creatorId = matchData.createdBy ?? matchData.created_by;
          const isCreator = creatorId != null && Number(creatorId) === Number(user.id);
          const canEdit = matchData.userPermissions
            ? !!matchData.userPermissions.canEdit
            : isAdmin || isCreator;

          if (!canEdit) {
            setError("You don't have permission to edit this match. Only the match host can make changes.");
            setLoading(false);
            return;
          }
          
          setMatch(matchData);
          
          // Set form data from match (mapping API response fields to form fields)
          const loadedForm = {
            match_name: matchData.matchName || "",
            sport_id: matchData.sportId || "",
            tournament_id: matchData.tournamentId || null,
            participant_1_id: matchData.participant1Id || "",
            participant_2_id: matchData.participant2Id || "",
            // The time at the venue, wherever the host is editing from
            scheduled_at: matchData.scheduledAt ? toEventInputValue(matchData.scheduledAt, eventTimeZone(matchData)) : "",
            venue: matchData.venue || "",
            venue_address: matchData.venueAddress || "",
            venue_lat: matchData.venueLat || null,
            venue_lng: matchData.venueLng || null,
            venue_place_id: matchData.placeId || "",
            court: matchData.court || "",
            description: matchData.notes || "", // notes field in API
            referee_id: matchData.refereeId || "",
            status: matchData.status || "scheduled"
          };
          setFormData(loadedForm);
          setInitialForm(loadedForm);
        } else {
          const errorMsg = matchResponse?.message || "Match not found";
          setError(`Failed to load match: ${errorMsg}`);
        }

        setSports(sportsResponse.data || []);
        // getAllUsers returns { users: [...], pagination: {...} }
        const rawUsers = usersResponse?.data?.users || usersResponse?.data || [];
        setUsers(Array.isArray(rawUsers) ? rawUsers : []);

      } catch (err) {
        if (err.status === 401) {
          setError("Authentication required. Please log in again.");
        } else if (err.status === 403) {
          setError("You don't have permission to edit this match.");
        } else if (err.status === 404) {
          setError("Match not found.");
        } else {
          setError(`Failed to load match: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [matchId, isAuthenticated, user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Note: Only venue, scheduled_at, notes, and referee_id can be updated
    // Core match details like participants and sport cannot be changed after creation

    try {
      setSaving(true);
      setError("");
      
      // Only the fields that changed; once the match has left "scheduled"
      // only the notes can change (the backend refuses the rest).
      const updateData = buildMatchUpdatePayload(initialForm || {}, formData, { notesOnly: isLocked });
      if (Object.keys(updateData).length === 0) {
        setSuccess("Nothing to update.");
        return;
      }

      const response = await matchService.updateMatch(matchId, updateData);
      
      if (response.success) {
        setSuccess("Match updated successfully!");
        setTimeout(() => {
          navigate(`/matches/${eid(match)}`);
        }, 1500);
      } else {
        setError(response.message || "Failed to update match");
      }
    } catch (err) {
      console.error("Error updating match:", err);
      setError("Failed to update match. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <GameLayout>
        <div className="matches-gaming-layout">
          <div className="loading-gaming-floating">
            <FloatingCard variant="primary" size="large" className="delay-1">
              <div className="loading-content-gaming">
                <div className="loading-spinner-gaming"></div>
                <h3>Loading Match Details...</h3>
                <p>Preparing match data for editing</p>
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!match) {
    // Separate error state — show the real reason rather than a generic "not found"
    if (error) {
      return (
        <GameLayout>
          <div className="matches-gaming-layout">
            <div className="error-gaming-floating">
              <FloatingCard variant="danger" size="large" className="delay-1">
                <div className="error-content-gaming">
                  <div className="error-icon">🚫</div>
                  <h3>Cannot Edit Match</h3>
                  <p>{error}</p>
                  <FloatingButton
                    variant="primary"
                    size="large"
                    onClick={() => navigate("/matches/manage")}
                    icon="←"
                  >
                    <span className="btn-text">Back to Matches</span>
                  </FloatingButton>
                </div>
              </FloatingCard>
            </div>
          </div>
        </GameLayout>
      );
    }
    return (
      <GameLayout>
        <div className="matches-gaming-layout">
          <div className="error-gaming-floating">
            <FloatingCard variant="danger" size="large" className="delay-1">
              <div className="error-content-gaming">
                <div className="error-icon">❌</div>
                <h3>Match Not Found</h3>
                <p>The requested match could not be found.</p>
                <FloatingButton
                  variant="primary"
                  size="large"
                  onClick={() => navigate("/matches/manage")}
                  icon="←"
                >
                  <span className="btn-text">Back to Matches</span>
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout>
      <div className="match-creation-gaming">
        {/* Header Card */}
        <div className="header-card-gaming">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="header-content-gaming">
              <h1 className="match-title">✏️ Edit Match</h1>
              <p className="match-subtitle">Update your epic battle details</p>
            </div>
          </FloatingCard>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="match-form-gaming">
          {/* Match Details Section */}
          <div className="form-section-gaming">
            <FloatingCard variant="secondary" size="large" className="delay-2">
              <div className="section-content">
                <h2 className="form-section-title">🎯 Match Details</h2>
                
                <div className="form-row-gaming">
                  <div className="form-group-gaming">
                    <label className="form-label-gaming">Match Name <span className="readonly-badge">📖 Read Only</span></label>
                    <input
                      type="text"
                      value={formData.match_name}
                      className="form-input-gaming form-input-readonly"
                      placeholder="Match name cannot be changed"
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-group-gaming">
                    <label className="form-label-gaming">Sport <span className="readonly-badge">📖 Read Only</span></label>
                    <select
                      value={formData.sport_id}
                      className="form-select-gaming form-select-readonly"
                      disabled
                    >
                      <option value="">Select a sport</option>
                      {sports.map((sport) => (
                        <option key={sport.id} value={sport.id}>
                          {sport.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row-gaming">
                  <div className="form-group-gaming">
                    <label className="form-label-gaming">Scheduled Date & Time{isLocked && <span className="readonly-badge"> 📖 Read Only</span>}</label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_at}
                      onChange={(e) => handleInputChange("scheduled_at", e.target.value)}
                      className={isLocked ? "form-input-gaming form-input-readonly" : "form-input-gaming"}
                      disabled={isLocked}
                      min={isLocked ? undefined : (() => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); })()}
                    />
                  </div>
                </div>

                {isLocked && (
                  <div className="form-hint" data-testid="match-edit-locked">
                    This match is {String(match?.status || formData.status).replace(/_/g, " ")} — only the description can still be changed.
                  </div>
                )}

                <fieldset disabled={isLocked} style={{ border: 0, padding: 0, margin: 0 }}>
                <div className="form-group-gaming full-width">
                  <label className="form-label-gaming">Venue (Optional) ✏️</label>
                  <VenueSearch
                    label=""
                    value={{
                      venue: formData.venue,
                      venue_place_id: formData.venue_place_id,
                      venue_address: formData.venue_address,
                      venue_lat: formData.venue_lat,
                      venue_lng: formData.venue_lng,
                      court: formData.court,
                    }}
                    onChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        venue: v.venue,
                        venue_address: v.venue_address,
                        venue_lat: v.venue_lat,
                        venue_lng: v.venue_lng,
                        venue_place_id: v.venue_place_id,
                        court: v.court,
                      }))
                    }
                  />
                </div>

                <div className="form-group-gaming full-width">
                  <label className="form-label-gaming">Referee (Optional) ✏️</label>
                  <select
                    value={formData.referee_id}
                    onChange={(e) => handleInputChange("referee_id", e.target.value)}
                    className="form-select-gaming"
                  >
                    <option value="">Select a referee (optional)</option>
                    {users.length > 0 ? (
                      users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} (@{user.username})
                        </option>
                      ))
                    ) : (
                      <option disabled>Users not available - authentication required</option>
                    )}
                  </select>
                  {users.length === 0 && (
                    <small style={{color: 'rgba(255,255,255,0.7)', marginTop: '0.25rem', fontSize: '0.8rem', display: 'block'}}>
                      ⚠️ User list requires authentication. You can still edit venue, schedule, and description.
                    </small>
                  )}
                </div>
                </fieldset>

                <div className="form-group-gaming full-width">
                  <label className="form-label-gaming">Description (Optional) ✏️</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    className="form-textarea-gaming"
                    placeholder="Describe this epic match..."
                    rows={4}
                  />
                </div>
              </div>
            </FloatingCard>
          </div>

          {/* Participants Section */}
          <div className="form-section-gaming">
            <FloatingCard variant="secondary" size="large" className="delay-3">
              <div className="section-content">
                <h2 className="form-section-title">👥 Participants</h2>
                
                <div className="form-row-gaming">
                  <div className="form-group-gaming">
                    <label className="form-label-gaming">Participant 1 <span className="readonly-badge">📖 Read Only</span></label>
                    <input
                      type="text"
                      value={match?.participant_1_name || formData.participant_1_id || ""}
                      className="form-input-gaming form-input-readonly"
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-group-gaming">
                    <label className="form-label-gaming">Participant 2 <span className="readonly-badge">📖 Read Only</span></label>
                    <input
                      type="text"
                      value={match?.participant_2_name || formData.participant_2_id || ""}
                      className="form-input-gaming form-input-readonly"
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </FloatingCard>
          </div>

          {/* Messages */}
          {error && (
            <div className="alert-card error">
              <FloatingCard variant="danger" size="medium" className="delay-4">
                <div className="alert-content">
                  <span className="alert-icon">❌</span>
                  <span className="alert-message">{error}</span>
                </div>
              </FloatingCard>
            </div>
          )}

          {success && (
            <div className="alert-card success">
              <FloatingCard variant="success" size="medium" className="delay-4">
                <div className="alert-content">
                  <span className="alert-icon">✅</span>
                  <span className="alert-message">{success}</span>
                </div>
              </FloatingCard>
            </div>
          )}

          {/* Actions */}
          <div className="form-actions-gaming">
            <FloatingCard variant="outline" size="medium" className="delay-5">
              <div className="form-actions-content">
                <FloatingButton
                  type="button"
                  variant="secondary"
                  size="large"
                  onClick={() => navigate("/matches/manage")}
                  icon="←"
                >
                  <span className="btn-text">Cancel</span>
                </FloatingButton>

                <FloatingButton
                  type="submit"
                  variant="success"
                  size="large"
                  disabled={saving}
                  loading={saving}
                  icon="💾"
                >
                  <span className="btn-text">
                    {saving ? "Updating..." : "Update Match"}
                  </span>
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        </form>
      </div>
    </GameLayout>
  );
};

export default MatchEdit;